import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import SectionLabel from "../components/SectionLabel";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { IMAGES } from "../data/content";

const DOWNLOADS = [
  {
    key: "master",
    title: "Livinora · Master-Broschüre",
    copy: "Überblick über Marke, Konzepte, Stilserien und Paketlogik.",
    file: "/livinora-broschuere.pdf",
    cta: "Broschüre öffnen",
  },
  {
    key: "styles",
    title: "Stilserien-Katalog",
    copy: "Die fünf Stilwelten im Detail — Material, Palette, Anwendung.",
    file: "/livinora-broschuere.pdf",
    cta: "Katalog öffnen",
  },
  {
    key: "commercial",
    title: "Gewerbe-Dossier",
    copy: "Skalierbare Lösungen für Betreiber, Parks und Entwickler.",
    file: "/livinora-broschuere.pdf",
    cta: "Dossier öffnen",
  },
];

export default function Broschuere() {
  useDocumentTitle(
    "Broschüre & Katalog",
    "Livinora-Broschüre, Stilserien-Katalog und Gewerbe-Dossier — ruhig, editorial, auf den Punkt."
  );

  return (
    <div data-testid="page-broschuere">
      {/* Hero */}
      <section className="pt-28 sm:pt-32 md:pt-44 pb-16 md:pb-24">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel index="∞" text="Broschüre & Downloads" />
            </Reveal>
            <Reveal delay={60}>
              <h1 className="display-xl mt-8 text-[44px] sm:text-[60px] md:text-[80px] lg:text-[96px] text-balance">
                Livinora zum Durchblättern.
              </h1>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora prose-livinora--lead mt-9 max-w-xl">
                Ruhig, editorial, auf den Punkt. Unsere Unterlagen sind als Vorschau zum
                Durchblättern gedacht — die finalen Versionen senden wir Ihnen auf Anfrage
                personalisiert zu.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal variant="mask">
              <div className="media-wrap aspect-[4/5]">
                <img src={IMAGES.brochure} alt="Broschüre" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Downloads */}
      <section className="pb-24 md:pb-32">
        <div className="container-livinora grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-[color:var(--livinora-hairline)]">
          {DOWNLOADS.map((d, i) => (
            <Reveal key={d.key} delay={i * 80}>
              <div
                className={`p-8 md:p-10 border-b md:border-b-0 border-[color:var(--livinora-hairline)] ${
                  i !== 2 ? "md:border-r" : ""
                } h-full flex flex-col`}
              >
                <span className="micro-label">0{i + 1}</span>
                <h2 className="serif text-2xl md:text-3xl mt-3 leading-[1.05]">{d.title}</h2>
                <p className="mt-4 text-[color:var(--livinora-muted)] leading-relaxed flex-1 text-sm">
                  {d.copy}
                </p>
                <a
                  href={d.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-livinora btn-ghost mt-10 self-start"
                  data-testid={`download-${d.key}`}
                >
                  {d.cta} ↗
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="container-livinora mt-10">
          <p className="text-xs text-[color:var(--livinora-muted)] max-w-2xl leading-relaxed">
            Hinweis: Die hinterlegten Vorschau-Dokumente befinden sich in finaler redaktioneller
            Bearbeitung. Für die aktuelle Vollversion mit allen Stilserien, Paketen und
            Referenzdaten nutzen Sie bitte das Anfrageformular unten.
          </p>
        </div>
      </section>

      {/* Personal */}
      <section className="section-pad bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] atmosphere-dark noise">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <Reveal>
              <span className="section-label section-label--inverse">
                <span className="section-label-num">∞</span>
                <span className="section-label-rule" aria-hidden="true" />
                <span className="section-label-text">Persönlich</span>
              </span>
            </Reveal>
            <Reveal delay={60}>
              <h2 className="display-xl mt-7 text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
                Lieber ein Gespräch als ein PDF?
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-7 text-[color:var(--livinora-stone)] leading-relaxed max-w-xl text-base md:text-[17px]">
                Wir stellen Livinora gerne persönlich vor — digital oder vor Ort. Maßgeschneiderte
                Unterlagen senden wir Ihnen anschließend gezielt zum Projekt zu.
              </p>
            </Reveal>
          </div>
          <Reveal delay={220} className="lg:col-span-4 lg:text-right">
            <Link
              to="/kontakt"
              className="btn-livinora btn-magnetic bg-[color:var(--livinora-paper)] text-[color:var(--livinora-ink)] hover:bg-white"
              data-testid="brochure-contact"
            >
              Beratung anfragen <span>→</span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
